import { useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/safeClient';

interface AnalyticsEvent {
  user_id: string | null;
  session_id: string;
  event_type: string;
  event_name: string;
  page_path: string | null;
  referrer_path: string | null;
  metadata: Record<string, any>;
  device_type: string | null;
}

function getSessionId(): string {
  let sid = sessionStorage.getItem('analytics_session_id');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('analytics_session_id', sid);
  }
  return sid;
}

function getDeviceType(): string {
  const w = window.innerWidth;
  if (w < 768) return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

export function useAnalytics() {
  const { user } = useAuth();
  const location = useLocation();
  const queue = useRef<AnalyticsEvent[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPath = useRef<string | null>(null);
  const sessionInitialized = useRef(false);

  const flush = useCallback(async () => {
    if (queue.current.length === 0) return;
    const batch = [...queue.current];
    queue.current = [];

    try {
      await supabase.from('analytics_events').insert(batch as any);
    } catch (e) {
      console.error('[Analytics] flush error', e);
    }
  }, []);

  const enqueue = useCallback((event: Omit<AnalyticsEvent, 'session_id' | 'user_id' | 'device_type'>) => {
    queue.current.push({
      ...event,
      user_id: user?.id ?? null,
      session_id: getSessionId(),
      device_type: getDeviceType(),
    });
  }, [user?.id]);

  // Initialize session
  useEffect(() => {
    if (sessionInitialized.current) return;
    sessionInitialized.current = true;

    const sid = getSessionId();
    const deviceType = getDeviceType();

    supabase.from('analytics_sessions').upsert({
      session_id: sid,
      user_id: user?.id ?? null,
      started_at: new Date().toISOString(),
      page_count: 1,
      device_type: deviceType,
      entry_page: location.pathname,
      exit_page: location.pathname,
    } as any, { onConflict: 'session_id' }).then(() => {});
  }, []);

  // Batch flush timer
  useEffect(() => {
    timerRef.current = setInterval(flush, 2000);
    const handleUnload = () => {
      flush();
      // Update session end
      const sid = getSessionId();
      navigator.sendBeacon?.(
        `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_sessions?session_id=eq.${sid}`,
        JSON.stringify({ ended_at: new Date().toISOString(), exit_page: location.pathname })
      );
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      window.removeEventListener('beforeunload', handleUnload);
      flush();
    };
  }, [flush, location.pathname]);

  // Auto-track pageviews
  useEffect(() => {
    const referrer = prevPath.current;
    prevPath.current = location.pathname;

    enqueue({
      event_type: 'pageview',
      event_name: `view_${location.pathname.replace(/\//g, '_').replace(/^_/, '') || 'home'}`,
      page_path: location.pathname,
      referrer_path: referrer,
      metadata: {},
    });

    // Update session page count & exit page
    supabase.from('analytics_sessions').update({
      exit_page: location.pathname,
      page_count: undefined, // We'll handle this with a raw increment approach below
      ended_at: new Date().toISOString(),
    } as any).eq('session_id', getSessionId()).then(() => {});
  }, [location.pathname, enqueue]);

  const trackClick = useCallback((eventName: string, metadata: Record<string, any> = {}) => {
    enqueue({
      event_type: 'click',
      event_name: eventName,
      page_path: location.pathname,
      referrer_path: null,
      metadata,
    });
  }, [enqueue, location.pathname]);

  const trackFunnelStep = useCallback((funnelName: string, stepNumber: number, stepName: string) => {
    enqueue({
      event_type: 'funnel_step',
      event_name: `${funnelName}_step_${stepNumber}`,
      page_path: location.pathname,
      referrer_path: null,
      metadata: { funnel: funnelName, step: stepNumber, step_name: stepName },
    });
  }, [enqueue, location.pathname]);

  return { trackClick, trackFunnelStep };
}
